import { motion, type Transition } from "motion/react";
import { useReducedMotion } from "../hooks/use-reduced-motion";
import { cn } from "../lib/cn";

const petals = [
  {
    name: "sage",
    color: "#49836e",
    path: "M45.05,79.15c-2.82,3.03-6.09,5.17-10,6.52-5.31,1.84-10.79,1.68-16.1-.11-3.44-1.16-6.55-3-9.25-5.42C2.42,73.64-.99,63.92.25,54.27c.48-3.69,1.55-7.27,3.33-10.54,1.91-3.57,4.55-6.64,7.77-9.08,3.69-2.79,8.01-4.58,12.68-5.14.47,7.56,3.6,14.5,9.09,19.71,2.23,2.1,4.76,3.72,7.55,4.96,1.57.7,2.95,1.64,4.17,2.83,2.06,2.01,3.46,4.47,4.17,7.25.78,3.03.66,6.14-.38,9.1-.77,2.18-2.01,4.1-3.58,5.79Z",
    origin: { x: -80, y: -65, rotate: 230, overshootX: 7, overshootY: 5 },
  },
  {
    name: "lilac",
    color: "#a494c5",
    path: "M58.29,45.27c-3.41,3.17-8.01,4.53-12.57,3.8-2.97-.48-5.72-1.72-7.94-3.75-1.48-1.35-2.8-2.84-3.89-4.54-2.57-3.99-3.94-8.64-3.84-13.4s1.5-9.33,3.89-13.41c3.62-6.17,9.8-10.85,16.65-12.83,5.54-1.6,11.58-1.56,17.01.42,9.57,3.47,16.19,11.72,17.41,21.9-6.88.81-13.35,4.12-18.13,9.11-2.17,2.27-3.89,4.83-5.15,7.69-.83,1.89-1.93,3.58-3.46,5Z",
    origin: { x: 65, y: -80, rotate: -230, overshootX: -5, overshootY: 7 },
  },
  {
    name: "purple",
    color: "#5f4d8c",
    path: "M92.11,86.79c-.49-7.52-3.49-14.48-8.96-19.7-2.41-2.3-4.96-3.85-7.99-5.2-1.15-.51-2.15-1.17-3.12-1.98-1.98-1.67-3.49-3.74-4.42-6.17-1.27-3.33-1.41-6.96-.4-10.38.53-1.78,1.37-3.38,2.5-4.84,3.27-4.23,7.9-7.2,13.08-8.52s10.67-.94,15.67,1.04c7.79,3.08,13.8,9.94,16.24,17.93,1.74,5.71,1.82,11.78.22,17.52-1.25,4.49-3.6,8.6-6.78,11.97-3.38,3.59-7.63,6.19-12.36,7.55-1.22.35-2.39.66-3.68.79Z",
    origin: { x: 80, y: 65, rotate: 230, overshootX: -7, overshootY: -5 },
  },
  {
    name: "peach",
    color: "#e09a6a",
    path: "M65.94,114.92c-9.22,2.79-19.01.57-26.34-5.58-5.06-4.3-8.26-9.96-9.36-16.54,8.03-.72,15.45-4.79,20.48-11.03,1.39-1.77,2.58-3.68,3.4-5.76,1.91-4.86,6.27-8.25,11.43-8.99,3.83-.55,7.73,.25,10.95,2.43,.9,.61,1.69,1.26,2.48,2.01,2.48,2.38,4.39,5.24,5.59,8.47,1.12,3.02,1.71,6.18,1.6,9.42-.14,4.37-1.36,8.6-3.53,12.38-1.07,1.86-2.35,3.53-3.79,5.11-3.44,3.77-8.02,6.59-12.92,8.08Z",
    origin: { x: -65, y: 80, rotate: -230, overshootX: 5, overshootY: -7 },
  },
] as const;

export type SamklangLoaderVariant = "static" | "spin" | "swirl";

export type SamklangLoaderProps = {
  readonly className?: string;
  readonly label?: string;
  readonly size?: "sm" | "md" | "lg" | "xl";
  readonly variant?: SamklangLoaderVariant;
};

const sizes = {
  sm: "h-4 w-4",
  md: "h-6 w-6",
  lg: "h-14 w-14",
  xl: "h-20 w-20",
} as const;

const fillBox = {
  transformBox: "fill-box",
  transformOrigin: "center",
} as const;

const swirlTransition: Transition = {
  duration: 0.9,
  ease: [0.25, 0.46, 0.45, 0.94],
  times: [0, 0.68, 1],
};

/** The official Samklang loader and one-shot logo reveal from the design source. */
export function SamklangLoader({
  className,
  label = "Laddar",
  size = "md",
  variant = "spin",
}: SamklangLoaderProps) {
  const reduceMotion = useReducedMotion();
  const activeVariant = reduceMotion ? "static" : variant;
  const paths = petals.map((petal, index) => {
    const swirl = activeVariant === "swirl";
    return (
      <motion.path
        key={petal.name}
        d={petal.path}
        fill={petal.color}
        style={fillBox}
        {...(swirl
          ? {
              initial: {
                x: petal.origin.x,
                y: petal.origin.y,
                scale: 0.08,
                rotate: petal.origin.rotate,
                opacity: 0,
              },
              animate: {
                x: [petal.origin.x, petal.origin.overshootX, 0],
                y: [petal.origin.y, petal.origin.overshootY, 0],
                scale: [0.08, 1.08, 1],
                rotate: [petal.origin.rotate, index % 2 === 0 ? -8 : 8, 0],
                opacity: [0, 1, 1],
              },
              transition: { ...swirlTransition, delay: index * 0.14 },
            }
          : {})}
      />
    );
  });

  return (
    <svg
      viewBox="0 0 116.08 116.11"
      role={variant === "spin" ? "status" : "img"}
      aria-label={label}
      aria-live={variant === "spin" ? "polite" : undefined}
      data-motion={activeVariant}
      focusable="false"
      className={cn("shrink-0 overflow-visible", sizes[size], className)}
      xmlns="http://www.w3.org/2000/svg"
    >
      {activeVariant === "spin" ? (
        <motion.g
          style={fillBox}
          animate={{ rotate: 360 }}
          transition={{ duration: 2.5, ease: "linear", repeat: Infinity }}
        >
          <motion.g
            style={fillBox}
            animate={{ rotate: [0, 40, 18, 46, 0] }}
            transition={{ duration: 3.9, ease: "easeInOut", repeat: Infinity }}
          >
            {paths}
          </motion.g>
        </motion.g>
      ) : (
        <g>{paths}</g>
      )}
    </svg>
  );
}
